import {
  ApiError,
  DocEditConflict,
  fetchDocHistory,
  fetchDocRevision,
  fetchMarkdown,
  saveDocEdit,
} from "./api";
import { state } from "./app-state";
import { escapeHtml } from "./comments";
import { getDocById } from "./constants";
import { computeDiffBlocks, diffHasChanges, renderDiffHtml } from "./diff";
import { ui } from "./dom";
import { openDocument } from "./document-view";
// history.ts ⇄ editor.ts 互相引用（冲突对比取编辑区内容 / 恢复走保存流程），
// 调用均在运行期函数体内，ESM live binding 下安全。
import { getEditorContent, saveEditor } from "./editor";
import {
  buildRestoreNote,
  canEditDoc,
  contentPathOf,
  formatCharsDelta,
  needsLeaveConfirm,
  normalizeContent,
} from "./editor-core";
import { confirmDialog } from "./modal";
import { hardLogout } from "./session-ui";
import { showToast } from "./toast";
import type { DocRevisionMeta } from "./types";

let revisions: DocRevisionMeta[] = [];
let revisionCache = new Map<number, string>();
let selectedId: number | null = null;
let conflictMode = false;
let historyDocId = "";
let historyPath = "";
let restoreFocus: HTMLElement | null = null;

export function isHistoryOpen(): boolean {
  return !ui.historyMask.hidden;
}

function emptyState(message: string): string {
  return `<div class="empty-state history-empty">${message}</div>`;
}

function historySkeleton(rows = 4): string {
  const row = `<div class="history-skeleton" aria-hidden="true">
    <span></span><span></span>
  </div>`;
  return `<div class="history-skeleton-group" aria-label="正在载入版本列表">${row.repeat(rows)}</div>`;
}

function formatRevisionTime(timestamp: number): string {
  try {
    return new Date(timestamp).toLocaleString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

function originBadge(origin: DocRevisionMeta["origin"]): string {
  if (origin === "baseline") {
    return '<span class="history-origin history-origin--baseline">发布基线</span>';
  }
  if (origin === "publish") {
    return '<span class="history-origin history-origin--publish">本地发布</span>';
  }
  return '<span class="history-origin history-origin--web">在线编辑</span>';
}

function renderRevisionList(): void {
  ui.historyList.innerHTML = revisions
    .map((revision, index) => {
      const older = revisions[index + 1] ?? null;
      const delta = formatCharsDelta(revision.size, older ? older.size : null);
      const active = revision.id === selectedId;
      const fallbackNote = origin === "baseline"
        ? "发布内容基线"
        : origin === "publish"
          ? "本地发布"
          : "—";
      return `<button type="button" class="history-item${active ? " active" : ""}" data-rev-id="${revision.id}"${active ? ' aria-current="true"' : ""}>
        <span class="history-item-top">
          <b>#${revision.id}</b>
          <span>${escapeHtml(formatRevisionTime(revision.created_at))}</span>
          ${index === 0 ? '<span class="history-current">当前</span>' : ""}
        </span>
        <span class="history-item-note">${escapeHtml(revision.note || fallbackNote)}</span>
        <span class="history-item-meta">${originBadge(revision.origin)}<span>${escapeHtml(delta)}</span></span>
      </button>`;
    })
    .join("");
}

async function revisionContent(id: number): Promise<string> {
  const cached = revisionCache.get(id);
  if (cached !== undefined) return cached;
  const revision = await fetchDocRevision(historyPath, id);
  const normalized = normalizeContent(revision.content);
  revisionCache.set(id, normalized);
  return normalized;
}

async function selectRevision(id: number): Promise<void> {
  if (conflictMode) return;
  selectedId = id;
  renderRevisionList();
  const index = revisions.findIndex((revision) => revision.id === id);
  if (index < 0) return;
  const older = revisions[index + 1] ?? null;
  ui.historyDialog.classList.add("is-detail");
  ui.historyRestoreBtn.hidden = index === 0;
  ui.historyDiffLabel.textContent = older ? `版本#${older.id} → 版本#${id}` : `版本#${id}（最早记录）`;
  ui.historyDiff.innerHTML = emptyState("正在载入差异…");
  ui.historyStatus.textContent = "正在载入差异";
  try {
    const [oldContent, newContent] = await Promise.all([
      older ? revisionContent(older.id) : Promise.resolve(""),
      revisionContent(id),
    ]);
    if (selectedId !== id || conflictMode) return;
    const blocks = computeDiffBlocks(oldContent, newContent);
    ui.historyDiff.innerHTML = diffHasChanges(blocks)
      ? renderDiffHtml(blocks)
      : emptyState("与上一版内容一致");
    ui.historyDiff.scrollTop = 0;
    ui.historyStatus.textContent = "差异已载入";
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      closeHistory();
      hardLogout();
      return;
    }
    if (selectedId !== id) return;
    ui.historyDiff.innerHTML = emptyState(
      `差异载入失败：${escapeHtml(error instanceof Error ? error.message : "请稍后重试")}`,
    );
  }
}

async function loadRevisions(): Promise<void> {
  ui.historyList.innerHTML = historySkeleton();
  try {
    revisions = await fetchDocHistory(historyPath);
    revisionCache = new Map();
    selectedId = null;
    renderRevisionList();
    ui.historyStatus.textContent = `已载入 ${revisions.length} 个版本`;
    if (!revisions.length) {
      ui.historyDiffLabel.textContent = "";
      ui.historyRestoreBtn.hidden = true;
      ui.historyDiff.innerHTML = emptyState(
        "还没有编辑历史。第一次在网页上保存后，这里会出现「发布基线」与每次修改的版本。",
      );
      return;
    }
    void selectRevision(revisions[0]!.id);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      closeHistory();
      hardLogout();
      return;
    }
    ui.historyList.innerHTML = `<div class="empty-state history-empty">载入失败：${escapeHtml(
      error instanceof Error ? error.message : "请稍后重试",
    )}<br /><button type="button" class="secondary-button" data-history-retry>重试</button></div>`;
  }
}

async function renderConflictDiff(): Promise<void> {
  const doc = getDocById(historyDocId);
  if (!doc) return;
  ui.historyList.innerHTML = "";
  ui.historyRestoreBtn.hidden = true;
  ui.historyDiffLabel.textContent = "服务器最新版 → 我的未保存修改";
  ui.historyDiff.innerHTML = emptyState("正在载入服务器最新版…");
  try {
    const source = await fetchMarkdown(doc.file);
    const blocks = computeDiffBlocks(
      normalizeContent(source.text),
      normalizeContent(getEditorContent()),
    );
    ui.historyDiff.innerHTML = diffHasChanges(blocks)
      ? renderDiffHtml(blocks)
      : emptyState("两边内容一致，可直接「载入最新版重编」后保存");
    ui.historyStatus.textContent = "冲突对比已载入";
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      closeHistory();
      hardLogout();
      return;
    }
    ui.historyDiff.innerHTML = emptyState(
      `载入失败：${escapeHtml(error instanceof Error ? error.message : "请稍后重试")}`,
    );
  }
}

export async function openHistory(options: { conflict?: boolean } = {}): Promise<void> {
  const doc = getDocById(state.currentDocId);
  if (!doc || !canEditDoc(doc, state.session)) return;
  conflictMode = !!options.conflict;
  historyDocId = doc.id;
  historyPath = contentPathOf(doc);
  restoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  ui.historyTitle.textContent = doc.shortTitle || doc.title;
  ui.historyDialog.classList.toggle("is-conflict", conflictMode);
  ui.historyDialog.classList.remove("is-detail");
  // 返回键仅在移动端 is-detail 布局下由 CSS 显示；冲突模式没有列表可返回。
  ui.historyBackBtn.hidden = conflictMode;
  ui.historyRestoreBtn.hidden = true;
  ui.historyDiffLabel.textContent = "";
  ui.historyDiff.innerHTML = "";
  ui.historyMask.hidden = false;
  document.body.classList.add("history-open");
  ui.historyCloseBtn.focus();

  if (conflictMode) {
    ui.historyDialog.classList.add("is-detail");
    await renderConflictDiff();
    return;
  }
  await loadRevisions();
}

export function closeHistory(): void {
  if (ui.historyMask.hidden) return;
  ui.historyMask.hidden = true;
  document.body.classList.remove("history-open");
  ui.historyList.innerHTML = "";
  ui.historyDiff.innerHTML = "";
  ui.historyDialog.classList.remove("is-detail", "is-conflict");
  conflictMode = false;
  selectedId = null;
  restoreFocus?.focus();
  restoreFocus = null;
}

async function restoreSelected(): Promise<void> {
  if (selectedId === null || conflictMode) return;
  const id = selectedId;
  let content: string;
  try {
    content = await revisionContent(id);
  } catch {
    showToast("载入该版本全文失败，请重试", "error");
    return;
  }
  const note = buildRestoreNote(id);

  if (state.editor) {
    // 编辑态下恢复：以历史版本全文另存新版本；未保存的修改先进草稿。
    if (needsLeaveConfirm(state.editor, getEditorContent())) {
      const proceed = await confirmDialog({
        kicker: "版本历史",
        title: `放弃当前未保存的修改并恢复至 版本#${id}？`,
        quote: "当前编辑区的文字会备份到本地草稿。",
        submitText: "继续恢复",
        danger: true,
      });
      if (!proceed) return;
    } else {
      const proceed = await confirmDialog({
        kicker: "版本历史",
        title: `恢复至 版本#${id}？`,
        quote: "将以该版本全文另存为一个新版本，现有历史不会丢失。",
        submitText: "恢复",
      });
      if (!proceed) return;
    }
    closeHistory();
    await saveEditor({ content, note });
    return;
  }

  const proceed = await confirmDialog({
    kicker: "版本历史",
    title: `恢复至 版本#${id}？`,
    quote: "将以该版本全文另存为一个新版本，现有历史不会丢失。",
    submitText: "恢复",
  });
  if (!proceed) return;

  const doc = getDocById(historyDocId);
  if (!doc) return;
  try {
    // 恢复前拿最新 release 作乐观锁基准，避免依赖打开文档时的缓存值。
    const source = await fetchMarkdown(doc.file);
    if (!source.release) {
      showToast("当前环境不支持在线编辑（缺少内容版本标头）", "error");
      return;
    }
    const result = await saveDocEdit({ path: historyPath, content, base_release: source.release, note });
    state.contentRelease.set(historyDocId, result.release);
    state.markdownCache.delete(historyDocId);
    closeHistory();
    await openDocument(historyDocId);
    showToast(result.unchanged ? "该版本与当前内容一致" : `已恢复至 版本#${id}`, result.unchanged ? "info" : "success");
  } catch (error) {
    if (error instanceof DocEditConflict) {
      showToast("正文刚有新的保存，已刷新版本列表，请重试", "error");
      await loadRevisions();
      return;
    }
    if (error instanceof ApiError && error.status === 401) {
      closeHistory();
      hardLogout();
      return;
    }
    showToast(error instanceof Error ? error.message : "恢复失败，请稍后重试", "error");
  }
}

export function bindHistoryEvents(): void {
  ui.historyBtn.addEventListener("click", () => void openHistory());
  ui.conflictDiffBtn.addEventListener("click", () => void openHistory({ conflict: true }));
  ui.historyCloseBtn.addEventListener("click", () => closeHistory());
  ui.historyBackBtn.addEventListener("click", () => {
    ui.historyDialog.classList.remove("is-detail");
  });
  ui.historyMask.addEventListener("click", (event) => {
    if (event.target === ui.historyMask) closeHistory();
  });
  ui.historyRestoreBtn.addEventListener("click", () => void restoreSelected());
  ui.historyList.addEventListener("click", (event) => {
    const retry = (event.target as Element).closest("[data-history-retry]");
    if (retry) {
      void loadRevisions();
      return;
    }
    const item = (event.target as Element).closest<HTMLButtonElement>("[data-rev-id]");
    if (item?.dataset.revId) void selectRevision(Number(item.dataset.revId));
  });
  ui.historyDiff.addEventListener("click", (event) => {
    const fold = (event.target as Element).closest<HTMLButtonElement>(".diff-fold");
    if (!fold) return;
    const group = fold.nextElementSibling;
    if (group instanceof HTMLElement) group.hidden = false;
    fold.remove();
  });
}
